// Every control the portal shows before its feature is built (UI pass, Oct 2026). Each one is a
// disabled control with the kit tooltip: the label, then one line on what it will do. Two labels
// only, from the spec: "Coming before go-live" (the rest of Phase 1) and "Phase 2" (the spec's phase
// tags). When a feature lands, its entry is deleted here and the control becomes real; the test in
// placeholders.test.ts fails while an entry is unused or a used id is missing. This list is also the
// remaining-work list.

export type PlaceholderLabel = 'Coming before go-live' | 'Phase 2';

export type Placeholder = {
  label: PlaceholderLabel;
  /** The spec section that describes the feature. */
  spec: string;
  /** One line, shown after the label in the tooltip. */
  what: string;
};

const soon = (spec: string, what: string): Placeholder => ({
  label: 'Coming before go-live',
  spec,
  what,
});

export const placeholders = {
  // Lists (My renewals, All renewals)
  'list.export': soon(
    'C12',
    'Downloads the department workbook for a month, the same file as the weekly export.',
  ),
  'list.scope': soon(
    'C7.3',
    'Switches between your accounts (you are the executive in AMS360) and the whole department.',
  ),
  'list.view.bank': soon('C7.3', 'Shows only renewals with a bank (mortgagee) to send proof to.'),
  'list.view.pay': soon('C7.3', 'Shows only renewals whose carrier renews after payment.'),
  'list.view.save': soon('C7.3', 'Saves the filters, sort and grouping as your own view.'),
  'list.filter.intake': soon(
    'C7.3',
    'Filters by how the dec page comes in: Download, Email or Online. Set per carrier in Settings.',
  ),
  'list.row.reopen': soon('C7.4', 'Opens a closed renewal again.'),
  'list.row.delete': soon(
    'C15a',
    'Deletes the record with a reason. It can be restored for 30 days.',
  ),
  'list.row.requestDelete': soon('C15a', 'Asks the Manager to delete the record, with a reason.'),
  'list.bulk.export': soon('C12', 'Downloads the selected renewals in the workbook format.'),
  // The Record
  'record.delete': soon(
    'C15a',
    'Deletes the record with a reason. It can be restored for 30 days.',
  ),
  'record.requestDelete': soon('C15a', 'Asks the Manager to delete the record, with a reason.'),
  // The AE's handoffs, on the Quick view and the Record banner (spec C6; S15 batches 6 and 7)
  // Settings
  'settings.carriers': soon(
    'C14',
    'Each carrier: intake path, portal, service email, auto fetch, pay to renew and policy number rules.',
  ),
  'settings.banks': soon(
    'C14',
    'Each bank: its AMS360 names, how proof goes (MyCoverageInfo, email or fax).',
  ),
  'settings.handoffs': soon(
    'C14',
    'Act by rules per handoff reason and the department default AE.',
  ),
  'settings.assignment': soon(
    'C14',
    'Who new renewals go to: ordered rules and the round-robin list.',
  ),
  'settings.export': soon('C12', 'The weekly export: day, time, recipients and column order.'),
  'settings.addStatus': soon('C4', 'Adds a custom status with its stage, color and sheet value.'),
  'settings.editUrgency': soon(
    'C5',
    'Changes the windows, bands and needs-attention rules for the department.',
  ),
  'settings.editDefaults': soon('C14', 'Changes these defaults for the department, with History.'),
} satisfies Record<string, Placeholder>;

export type PlaceholderId = keyof typeof placeholders;

/** The tooltip: "Coming before go-live. Moves the renewal to another person." */
export const placeholderTip = (id: PlaceholderId) =>
  `${placeholders[id].label}. ${placeholders[id].what}`;
